import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ChatProvider } from "./context/ChatContext";
import { LicenseProvider } from "./context/LicenseContext";
import ChatBubble from "./components/ChatBubble";
import PrivateRoute from "./components/PrivateRoute";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import OfflineIndicator from "./components/OfflineIndicator";
import Login from "./pages/Login";
import Register from "./pages/Register";
import NotFound from "./pages/NotFound";
import ForgotPassword from "./pages/ForgotPassword";
import AdmissionApplication from "./pages/public/AdmissionApplication";
import UserManagement from "./pages/admin/UserManagement";
import ActivityLogPage from "./pages/admin/ActivityLog";
import HolidayCalendar from "./pages/admin/HolidayCalendar";
import TeacherHolidays from "./pages/teacher/Holidays";
import Chat from "./pages/Chat";
// Admin Pages
import AdminDashboard from "./pages/admin/Dashboard";
import AdminProfile from "./pages/admin/Profile";
import AdminStudents from "./pages/admin/Students";
import AdminPromotions from "./pages/admin/Promotions";
import AdminCustomFields from "./pages/admin/CustomFields";
import AdminFeeStructures from "./pages/admin/FeeStructures";
import AdminDiscountDefinitions from "./pages/admin/DiscountDefinitions";
import AdminTeachers from "./pages/admin/Teachers";
import AdminClasses from "./pages/admin/Classes";
import AdminTransport from "./pages/admin/Transport";
import AdminFees from "./pages/admin/Fees";
import AdminAttendance from "./pages/admin/Attendance";
import AdminLeaves from "./pages/admin/Leaves";
import AdminReports from "./pages/admin/Reports";
import AdminSalary from "./pages/admin/Salary";
import AdminExpenses from "./pages/admin/Expenses";
import AdminNonTeachingStaff from "./pages/admin/NonTeachingStaff";
import AdminProgressReports from "./pages/admin/ProgressReports";
import AdminRolePermissions from "./pages/admin/RolePermissions";
import AdminExams from "./pages/admin/Exams";
import AdminCommunications from "./pages/admin/Communications";
import AdminSettings from "./pages/admin/Settings";
import AdminAdmissionForm from "./pages/admin/AdmissionForm";
import AdminApplications from "./pages/admin/Applications";

// Teacher Pages
import TeacherDashboard from "./pages/teacher/Dashboard";
import TeacherProfile from "./pages/teacher/Profile";
import TeacherAttendance from "./pages/teacher/Attendance";
import TeacherClass from "./pages/teacher/Class";
import TeacherStudents from "./pages/teacher/Students";
import TeacherLeaves from "./pages/teacher/Leaves";
import TeacherSubjects from "./pages/teacher/Subjects";
import TeacherDailyModules from "./pages/teacher/DailyModules";
import TeacherEvents from "./pages/teacher/Events";
import TeacherExams from "./pages/teacher/Exams";
import TeacherSelfieAttendance from "./pages/teacher/SelfieAttendance";
import TeacherTimetable from "./pages/teacher/Timetable";
import TeacherMaterials from "./pages/teacher/Materials";
import TeacherProgressReports from "./pages/teacher/ProgressReports";

// Parent Pages
import ParentDashboard from "./pages/parent/Dashboard";
import ParentProfile from "./pages/parent/Profile";
import ParentChild from "./pages/parent/Child";
import ParentAttendance from "./pages/parent/Attendance";
import ParentFees from "./pages/parent/Fees";
import ParentTransport from "./pages/parent/Transport";
import ParentDailyModules from "./pages/parent/DailyModules";
import ParentEvents from "./pages/parent/Events";
import ParentExams from "./pages/parent/Exams";
import ParentNotifications from "./pages/parent/Notifications";
import ParentTimetable from "./pages/parent/Timetable";
import ParentMaterials from "./pages/parent/Materials";
import ParentProgressReports from "./pages/parent/ProgressReports";

// Principal Pages
import PrincipalDashboard from "./pages/principal/Dashboard";
import PrincipalProfile from "./pages/principal/Profile";
import PrincipalTeachers from "./pages/principal/Teachers";
import PrincipalStudents from "./pages/principal/Students";
import PrincipalAttendance from "./pages/principal/Attendance";
import PrincipalLeaves from "./pages/principal/Leaves";
import PrincipalDailyModules from "./pages/principal/DailyModules";
import PrincipalEvents from "./pages/principal/Events";
import PrincipalSubjects from "./pages/principal/Subjects";
import PrincipalFees from "./pages/principal/Fees";
import PrincipalExpenses from "./pages/principal/Expenses";
import PrincipalNonTeachingStaff from "./pages/principal/NonTeachingStaff";
import PrincipalProgressReports from "./pages/principal/ProgressReports";
import PrincipalExams from "./pages/principal/Exams";
import PrincipalTimetable from "./pages/principal/Timetable";
import PrincipalMaterials from "./pages/principal/Materials";
import PrincipalAdmissionForm from "./pages/principal/AdmissionForm";
import PrincipalApplications from "./pages/principal/Applications";

// Super Admin Pages
import SuperAdminDashboard from "./pages/super-admin/Dashboard";
import SuperAdminProfile from "./pages/super-admin/Profile";
import AdminManagement from "./pages/super-admin/AdminManagement";
import LicenseManagement from "./pages/super-admin/LicenseManagement";
import SmsConfig from "./pages/super-admin/SmsConfig";
import EmailConfig from "./pages/super-admin/EmailConfig";
import PaymentConfig from "./pages/super-admin/PaymentConfig";
import AttendanceDeviceConfig from "./pages/super-admin/AttendanceDeviceConfig";
import SchoolManagement from "./pages/super-admin/SchoolManagement";

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <LicenseProvider>
        <ChatProvider>
        <Router>
          <div className="App">
            <PWAInstallPrompt />
            <OfflineIndicator />
            <ChatBubble />
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/admission-apply" element={<AdmissionApplication />} />
            <Route path="/" element={<Navigate to="/login" />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<Navigate to="/admin/dashboard" />} />
            <Route
              path="/admin/dashboard"
              element={
                <PrivateRoute allowedRoles={["admin"]}>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/profile"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/students"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminStudents />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/promotions"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "principal"]}>
                  <AdminPromotions />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/custom-fields"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminCustomFields />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/fee-structures"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "principal"]}>
                  <AdminFeeStructures />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/discount-definitions"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin", "principal"]}>
                  <AdminDiscountDefinitions />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/teachers"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminTeachers />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/classes"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminClasses />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/transport"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminTransport />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/fees"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminFees />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/attendance"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminAttendance />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/leaves"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminLeaves />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminReports />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/salary"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminSalary />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/non-teaching-staff"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminNonTeachingStaff />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/expenses"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminExpenses />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/progress-reports"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminProgressReports />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/role-permissions"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminRolePermissions />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/exams"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminExams />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/communications"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminCommunications />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminSettings />
                </PrivateRoute>
              }
            />

            <Route
              path="/admin/activity-log"
              element={
                <PrivateRoute allowedRoles={["admin", "principal", "super_admin"]}>
                  <ActivityLogPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <PrivateRoute allowedRoles={["admin", "principal", "class_teacher"]}>
                  <Chat />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/holidays"
              element={
                <PrivateRoute allowedRoles={["admin", "principal"]}>
                  <HolidayCalendar />
                </PrivateRoute>
              }
            />

            {/* Teacher Routes */}
            <Route
              path="/teacher"
              element={<Navigate to="/teacher/dashboard" />}
            />
            <Route
              path="/teacher/dashboard"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/profile"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/settings"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/selfie-attendance"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherSelfieAttendance />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/attendance"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherAttendance />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/class"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherClass />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/students"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherStudents />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/leaves"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherLeaves />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/subjects"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherSubjects />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/daily-modules"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherDailyModules />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/events"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherEvents />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/holidays"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherHolidays />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/exams"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherExams />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/progress-reports"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherProgressReports />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/timetable"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherTimetable />
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher/materials"
              element={
                <PrivateRoute allowedRoles={["class_teacher"]}>
                  <TeacherMaterials />
                </PrivateRoute>
              }
            />

            {/* Parent Routes */}
            <Route
              path="/parent"
              element={<Navigate to="/parent/dashboard" />}
            />
            <Route
              path="/parent/dashboard"
              element={
                <PrivateRoute allowedRoles={["parent"]}>
                  <ParentDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/parent/profile"
              element={
                <PrivateRoute allowedRoles={["parent"]}>
                  <ParentProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/parent/child"
              element={
                <PrivateRoute allowedRoles={["parent"]}>
                  <ParentChild />
                </PrivateRoute>
              }
            />
            <Route
              path="/parent/attendance"
              element={
                <PrivateRoute allowedRoles={["parent"]}>
                  <ParentAttendance />
                </PrivateRoute>
              }
            />
            <Route
              path="/parent/fees"
              element={
                <PrivateRoute allowedRoles={["parent"]}>
                  <ParentFees />
                </PrivateRoute>
              }
            />
            <Route
              path="/parent/transport"
              element={
                <PrivateRoute allowedRoles={["parent"]}>
                  <ParentTransport />
                </PrivateRoute>
              }
            />
            <Route
              path="/parent/daily-modules"
              element={
                <PrivateRoute allowedRoles={["parent"]}>
                  <ParentDailyModules />
                </PrivateRoute>
              }
            />
            <Route
              path="/parent/events"
              element={
                <PrivateRoute allowedRoles={["parent"]}>
                  <ParentEvents />
                </PrivateRoute>
              }
            />
            <Route
              path="/parent/exams"
              element={
                <PrivateRoute allowedRoles={["parent"]}>
                  <ParentExams />
                </PrivateRoute>
              }
            />
            <Route
              path="/parent/progress-reports"
              element={
                <PrivateRoute allowedRoles={["parent"]}>
                  <ParentProgressReports />
                </PrivateRoute>
              }
            />
            <Route
              path="/parent/notifications"
              element={
                <PrivateRoute allowedRoles={["parent"]}>
                  <ParentNotifications />
                </PrivateRoute>
              }
            />
            <Route
              path="/parent/timetable"
              element={
                <PrivateRoute allowedRoles={["parent"]}>
                  <ParentTimetable />
                </PrivateRoute>
              }
            />
            <Route
              path="/parent/materials"
              element={
                <PrivateRoute allowedRoles={["parent"]}>
                  <ParentMaterials />
                </PrivateRoute>
              }
            />

            {/* Principal Routes */}
            <Route
              path="/principal"
              element={<Navigate to="/principal/dashboard" />}
            />
            <Route
              path="/principal/dashboard"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/profile"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/teachers"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalTeachers />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/students"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalStudents />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/attendance"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalAttendance />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/leaves"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalLeaves />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/daily-modules"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalDailyModules />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/events"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalEvents />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/subjects"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalSubjects />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/fees"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalFees />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/expenses"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalExpenses />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/non-teaching-staff"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalNonTeachingStaff />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/progress-reports"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalProgressReports />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/exams"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalExams />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/timetable"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalTimetable />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/materials"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <PrincipalMaterials />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/settings"
              element={
                <PrivateRoute allowedRoles={["principal"]}>
                  <AdminSettings />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/admission"
              element={
                <PrivateRoute allowedRoles={["principal", "super_admin"]}>
                  <PrincipalAdmissionForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/principal/applications"
              element={
                <PrivateRoute allowedRoles={["principal", "super_admin"]}>
                  <PrincipalApplications />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <UserManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/admission"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminAdmissionForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/applications"
              element={
                <PrivateRoute allowedRoles={["admin", "super_admin"]}>
                  <AdminApplications />
                </PrivateRoute>
              }
            />

            {/* Super Admin Routes */}
            <Route
              path="/super-admin"
              element={<Navigate to="/super-admin/dashboard" />}
            />
            <Route
              path="/super-admin/dashboard"
              element={
                <PrivateRoute allowedRoles={["super_admin"]}>
                  <SuperAdminDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/super-admin/profile"
              element={
                <PrivateRoute allowedRoles={["super_admin"]}>
                  <SuperAdminProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/super-admin/admins"
              element={
                <PrivateRoute allowedRoles={["super_admin"]}>
                  <AdminManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/super-admin/license"
              element={
                <PrivateRoute allowedRoles={["super_admin"]}>
                  <LicenseManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/super-admin/sms-config"
              element={
                <PrivateRoute allowedRoles={["super_admin"]}>
                  <SmsConfig />
                </PrivateRoute>
              }
            />
            <Route
              path="/super-admin/email-config"
              element={
                <PrivateRoute allowedRoles={["super_admin"]}>
                  <EmailConfig />
                </PrivateRoute>
              }
            />
            <Route
              path="/super-admin/payment-config"
              element={
                <PrivateRoute allowedRoles={["super_admin"]}>
                  <PaymentConfig />
                </PrivateRoute>
              }
            />
            <Route
              path="/super-admin/attendance-device-config"
              element={
                <PrivateRoute allowedRoles={["super_admin"]}>
                  <AttendanceDeviceConfig />
                </PrivateRoute>
              }
            />
            <Route
              path="/super-admin/schools"
              element={
                <PrivateRoute allowedRoles={["super_admin"]}>
                  <SchoolManagement />
                </PrivateRoute>
              }
            />

            {/* Catch-all 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </Router>
      </ChatProvider>
        </LicenseProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
